import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { CheckCircle2, Star, Loader2, MessageSquareHeart } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QuestionField = ({ q, value, onChange }) => {
  const set = (v) => onChange(q.id, v);
  switch (q.type) {
    case 'section':
      return <div className="pt-2 pb-1 border-b"><h3 className="font-semibold text-lg">{q.label}</h3>{q.description && <p className="text-sm text-muted-foreground">{q.description}</p>}</div>;
    case 'long_text':
      return <Textarea rows={3} value={value || ''} onChange={(e) => set(e.target.value)} data-testid={`fb-q-${q.id}`} />;
    case 'number': case 'phone':
      return <Input type={q.type === 'number' ? 'number' : 'tel'} value={value || ''} onChange={(e) => set(e.target.value)} data-testid={`fb-q-${q.id}`} />;
    case 'email':
      return <Input type="email" value={value || ''} onChange={(e) => set(e.target.value)} data-testid={`fb-q-${q.id}`} />;
    case 'date':
      return <Input type="date" value={value || ''} onChange={(e) => set(e.target.value)} data-testid={`fb-q-${q.id}`} />;
    case 'single_choice': case 'dropdown':
      return (
        <Select value={value || ''} onValueChange={set}>
          <SelectTrigger data-testid={`fb-q-${q.id}`}><SelectValue placeholder="Select" /></SelectTrigger>
          <SelectContent>{(q.options || []).map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
        </Select>
      );
    case 'yes_no':
      return (
        <Select value={value || ''} onValueChange={set}>
          <SelectTrigger data-testid={`fb-q-${q.id}`}><SelectValue placeholder="Select" /></SelectTrigger>
          <SelectContent><SelectItem value="Yes">Yes</SelectItem><SelectItem value="No">No</SelectItem><SelectItem value="Partially">Partially</SelectItem></SelectContent>
        </Select>
      );
    case 'multi_choice':
      return (
        <div className="flex flex-wrap gap-2" data-testid={`fb-q-${q.id}`}>
          {(q.options || []).map(o => {
            const arr = Array.isArray(value) ? value : [];
            const on = arr.includes(o);
            return <button type="button" key={o} onClick={() => set(on ? arr.filter(x => x !== o) : [...arr, o])}
              className={`px-3 py-1.5 rounded-full text-sm border ${on ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white hover:bg-slate-50'}`}>{o}</button>;
          })}
        </div>
      );
    case 'rating_5': case 'star_10': {
      const max = q.type === 'star_10' ? 10 : 5;
      return (
        <div className="flex flex-wrap gap-1" data-testid={`fb-q-${q.id}`}>
          {Array.from({ length: max }, (_, i) => i + 1).map(n => (
            <button type="button" key={n} onClick={() => set(n)} className="p-1" aria-label={`rate ${n}`}>
              <Star className={`h-7 w-7 ${Number(value) >= n ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`} />
            </button>
          ))}
        </div>
      );
    }
    case 'nps': case 'slider': {
      const min = q.min ?? 0; const max = q.max ?? 10;
      return (
        <div className="flex flex-wrap gap-1.5" data-testid={`fb-q-${q.id}`}>
          {Array.from({ length: (max - min) + 1 }, (_, i) => min + i).map(n => (
            <button type="button" key={n} onClick={() => set(n)}
              className={`h-9 w-9 rounded-md border text-sm ${Number(value) === n ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white hover:bg-slate-50'}`}>{n}</button>
          ))}
        </div>
      );
    }
    case 'likert':
      return (
        <div className="flex flex-wrap gap-2" data-testid={`fb-q-${q.id}`}>
          {(q.options || []).map(o => (
            <button type="button" key={o} onClick={() => set(o)}
              className={`px-3 py-1.5 rounded-md text-sm border ${value === o ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white hover:bg-slate-50'}`}>{o}</button>
          ))}
        </div>
      );
    default:
      return <Input value={value || ''} onChange={(e) => set(e.target.value)} data-testid={`fb-q-${q.id}`} />;
  }
};

export default function PublicFeedbackPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [thankYou, setThankYou] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/public/cfv-feedback/${token}`);
      const d = await r.json();
      if (!r.ok) { setError(d.detail || 'This feedback link is invalid.'); return; }
      setData(d);
      if (d.submitted) { setDone(true); setThankYou(d.survey?.thank_you_message || 'Thank you for your feedback!'); }
      if (d.closed) setError('This feedback link has been closed.');
    } catch (e) {
      setError('Unable to load the feedback form.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const onChange = (qid, v) => setAnswers(prev => ({ ...prev, [qid]: v }));

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/public/cfv-feedback/${token}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ answers }),
      });
      const d = await r.json();
      if (!r.ok) { toast.error(d.detail || 'Submission failed'); return; }
      setThankYou(d.thank_you || 'Thank you for your feedback!');
      setDone(true);
    } catch (e) {
      toast.error('Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="h-8 w-8 animate-spin text-indigo-600" /></div>;

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <Card className="max-w-md w-full"><CardContent className="p-8 text-center space-y-3" data-testid="fb-error">
        <MessageSquareHeart className="h-12 w-12 text-slate-300 mx-auto" />
        <p className="text-slate-600">{error}</p>
      </CardContent></Card>
    </div>
  );

  if (done) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <Card className="max-w-md w-full"><CardContent className="p-10 text-center space-y-4" data-testid="fb-thankyou">
        <CheckCircle2 className="h-14 w-14 text-green-500 mx-auto" />
        <h2 className="text-xl font-semibold">Thank you!</h2>
        <p className="text-slate-600">{thankYou}</p>
      </CardContent></Card>
    </div>
  );

  const survey = data?.survey;
  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-2xl mx-auto space-y-4">
        <Card>
          <CardHeader className="border-b bg-indigo-600 text-white rounded-t-xl">
            <CardTitle className="text-xl" data-testid="fb-title">{survey?.title || 'Visitor Feedback'}</CardTitle>
            {survey?.description && <p className="text-indigo-100 text-sm">{survey.description}</p>}
            {data?.visitor?.name && <p className="text-indigo-100 text-sm mt-1">Hello {data.visitor.name}{data.visitor.company ? ` · ${data.visitor.company}` : ''}</p>}
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            {(survey?.questions || []).map(q => (
              <div key={q.id} className="space-y-2">
                {q.type !== 'section' && <Label className="text-sm font-medium">{q.label}{q.required && <span className="text-red-500"> *</span>}</Label>}
                {q.type !== 'section' && q.description && <p className="text-xs text-muted-foreground -mt-1">{q.description}</p>}
                <QuestionField q={q} value={answers[q.id]} onChange={onChange} />
              </div>
            ))}
            <Button className="w-full" size="lg" onClick={submit} disabled={submitting} data-testid="fb-submit">
              {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Submitting…</> : 'Submit Feedback'}
            </Button>
          </CardContent>
        </Card>
        <p className="text-center text-xs text-slate-400">Your responses are confidential and used only to improve your experience.</p>
      </div>
    </div>
  );
}
